/**
 * 言語コードをその言語自身での表記（日本語 / English / Tiếng Việt）にする。
 *
 * 言語切替メニューの選択肢は「今の表示言語」ではなく「切り替え先の言語」で名乗るべき
 * （英語表示中でも日本語話者が「日本語」を見つけられる）ので、翻訳ファイルを通さず
 * `Intl.DisplayNames` に任せる。翻訳ファイルに 3 言語同じ値を置く必要が無くなる。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 言語切替 UI に出す各言語の自国語表記を Intl から導出する
 * @graph-connects none
 */
import type { SupportedLanguage } from '../config.js';

/** 言語コードから自国語表記を引く関数。既定は `Intl.DisplayNames`。 @graph-connects none */
export type LanguageLabelResolver = (code: SupportedLanguage) => string | undefined;

/** @graph-connects none */
const resolveWithIntl: LanguageLabelResolver = (code) =>
  new Intl.DisplayNames([code], { type: 'language' }).of(code);

/**
 * @param code サポート言語コード（`ja` / `en` / `vi`）
 * @param resolve 表記の解決関数（テストで ICU データ欠落を再現するために差し替え可能）
 * @returns その言語自身での言語名。解決できない環境ではコードをそのまま返す
 * @graph-connects none
 */
export function getLanguageNativeLabel(
  code: SupportedLanguage,
  resolve: LanguageLabelResolver = resolveWithIntl,
): string {
  return resolve(code) ?? code;
}
